// useEnumOptionEditor.js

import { useCallback, useEffect, useMemo, useState } from 'react';

import { createEnumOptionId, normalizeEnumOptionValues, normalizeEnumOptions } from 'utilities/EnumOptions';
import { normalizeMeasurementDefaultUnit } from 'utilities/Measurement';

/**
 * Manages enum option editing state and handlers for the registry field editor.
 */
const useEnumOptionEditor = ({ selected, selectedIndex, patchSelected }) => {
  const [optionValueDrafts, setOptionValueDrafts] = useState({});

  const hasSelected = Boolean(selected);
  const isMeasurementField = selected?.type === 'measurement';
  const isOptionsField = selected?.type === 'enum' || selected?.type === 'multiselect' || isMeasurementField;
  const selectedOptions = selected?.options;

  const selectedEnumOptions = useMemo(() => (
    isOptionsField ? normalizeEnumOptions(selectedOptions) : []
  ), [isOptionsField, selectedOptions]);

  useEffect(() => {
    if (!hasSelected || !isOptionsField) {
      setOptionValueDrafts((prev) => (prev && Object.keys(prev).length ? {} : prev));
      return;
    }

    const optionsArray = normalizeEnumOptions(selectedOptions);

    setOptionValueDrafts((prev) => {
      const next = {};
      optionsArray.forEach((option) => {
        const optionId = option?.id || '';
        if (!optionId) return;
        if (prev && Object.prototype.hasOwnProperty.call(prev, optionId)) {
          next[optionId] = prev[optionId];
        }
      });

      const prevKeys = Object.keys(prev || {});
      const nextKeys = Object.keys(next);
      if (prevKeys.length === nextKeys.length) {
        const same = nextKeys.every((key) => prev?.[key] === next[key]);
        if (same) return prev;
      }
      if (prevKeys.length === 0 && nextKeys.length === 0) return prev || {};
      return next;
    });
  }, [hasSelected, isOptionsField, selectedIndex, selectedOptions]);

  const handleOptionAdd = useCallback(() => {
    if (selectedIndex < 0 || !selected || !isOptionsField) return;
    const existing = normalizeEnumOptions(selected.options);
    const base = 'Option';
    let counter = existing.length + 1;
    let candidate = `${base} ${counter}`;
    const existingValues = new Set(existing.map((opt) => opt.value));
    while (existingValues.has(candidate)) {
      counter += 1;
      candidate = `${base} ${counter}`;
    }
    patchSelected({
      options: [
        ...existing,
        {
          id: createEnumOptionId(),
          value: candidate,
          translations: { en: { label: candidate } },
          color: undefined,
          iconKey: undefined,
        },
      ],
    });
  }, [selectedIndex, selected, patchSelected, isOptionsField]);

  const handleOptionRemove = useCallback((optionId) => {
    if (selectedIndex < 0 || !selected || !isOptionsField) return;
    const existing = normalizeEnumOptions(selected.options);
    const target = existing.find((opt) => opt.id === optionId);
    if (!target) return;

    const optionValue = target.value;
    const nextOptions = existing.filter((opt) => opt.id !== optionId);
    const patch = { options: nextOptions };
    if (selected.default === optionValue) {
      patch.default = undefined;
    } else if (Array.isArray(selected.default) && selected.default.includes(optionValue)) {
      patch.default = selected.default.filter((entry) => entry !== optionValue);
    }
    if (isMeasurementField) {
      patch.defaultUnit = normalizeMeasurementDefaultUnit(selected.defaultUnit, nextOptions);
    }
    patchSelected(patch);
    setOptionValueDrafts((prev) => {
      if (!prev || !Object.prototype.hasOwnProperty.call(prev, optionId)) return prev;
      const next = { ...prev };
      delete next[optionId];
      return next;
    });
  }, [selectedIndex, selected, patchSelected, isMeasurementField, isOptionsField]);

  const handleOptionValueCommit = useCallback((optionId) => {
    if (selectedIndex < 0 || !selected || !isOptionsField) return;

    const normalized = normalizeEnumOptions(selected.options);
    const targetOption = normalized.find((option) => option.id === optionId);
    if (!targetOption) return;

    const optionValue = targetOption.value;

    const hasDraft = optionValueDrafts && Object.prototype.hasOwnProperty.call(optionValueDrafts, optionId);
    const rawDraft = hasDraft ? optionValueDrafts[optionId] : optionValue;
    const trimmed = rawDraft.trim();

    if (!trimmed) {
      handleOptionRemove(optionId);
      return;
    }

    if (trimmed === optionValue) {
      if (hasDraft) {
        setOptionValueDrafts((prev) => {
          if (!prev || !Object.prototype.hasOwnProperty.call(prev, optionId)) return prev;
          const next = { ...prev };
          delete next[optionId];
          return next;
        });
      }
      return;
    }

    const existingValues = new Set(
      normalizeEnumOptionValues(selected.options).filter((val) => val !== optionValue)
    );
    if (existingValues.has(trimmed)) {
      alert('Option values must be unique.');
      setOptionValueDrafts((prev) => ({
        ...(prev || {}),
        [optionId]: optionValue,
      }));
      return;
    }

    const nextOptions = normalized.map((opt) => {
      if (opt.id !== optionId) return opt;
      const translations = { ...(opt.translations || {}) };
      translations.en = { label: trimmed };
      return {
        ...opt,
        value: trimmed,
        translations,
      };
    });

    const patch = { options: nextOptions };
    if (selected.default === optionValue) {
      patch.default = trimmed;
    } else if (Array.isArray(selected.default) && selected.default.includes(optionValue)) {
      patch.default = selected.default.map((entry) => (entry === optionValue ? trimmed : entry));
    }
    if (isMeasurementField && selected.defaultUnit === optionValue) {
      patch.defaultUnit = trimmed;
    }
    patchSelected(patch);

    setOptionValueDrafts((prev) => {
      if (!prev || !Object.prototype.hasOwnProperty.call(prev, optionId)) return prev;
      const next = { ...prev };
      delete next[optionId];
      return next;
    });

  }, [selectedIndex, selected, optionValueDrafts, patchSelected, handleOptionRemove, isMeasurementField, isOptionsField]);

  const handleOptionColorChange = useCallback((optionId, nextColor) => {
    if (selectedIndex < 0 || !selected || !isOptionsField) return;

    const normalized = normalizeEnumOptions(selected.options);
    const nextOptions = normalized.map((opt) => (
      opt.id === optionId
        ? { ...opt, color: nextColor || undefined }
        : opt
    ));

    patchSelected({ options: nextOptions });
  }, [selectedIndex, selected, patchSelected, isOptionsField]);

  const handleOptionIconChange = useCallback((optionId, nextIconKey) => {
    if (selectedIndex < 0 || !selected || !isOptionsField) return;

    const normalizedIconKey = typeof nextIconKey === 'string'
      ? nextIconKey.trim()
      : '';
    const normalized = normalizeEnumOptions(selected.options);
    const nextOptions = normalized.map((opt) => (
      opt.id === optionId
        ? { ...opt, iconKey: normalizedIconKey || undefined }
        : opt
    ));

    patchSelected({ options: nextOptions });
  }, [selectedIndex, selected, patchSelected, isOptionsField]);

  const handleOptionMove = useCallback((fromOptionId, toOptionId) => {
    if (selectedIndex < 0 || !selected || !isOptionsField) return;
    if (!fromOptionId || !toOptionId || fromOptionId === toOptionId) return;

    const normalized = normalizeEnumOptions(selected.options);
    const fromIndex = normalized.findIndex((option) => option.id === fromOptionId);
    const toIndex = normalized.findIndex((option) => option.id === toOptionId);

    if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return;

    const nextOptions = [...normalized];
    const [moved] = nextOptions.splice(fromIndex, 1);
    const insertIndex = fromIndex < toIndex ? toIndex - 1 : toIndex;
    nextOptions.splice(insertIndex, 0, moved);

    patchSelected({ options: nextOptions });
  }, [selectedIndex, selected, patchSelected, isOptionsField]);

  const handleOptionValueKeyDown = useCallback((optionId, event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleOptionValueCommit(optionId);
      event.currentTarget.blur();
    }
  }, [handleOptionValueCommit]);

  return {
    selectedEnumOptions,
    optionValueDrafts,
    setOptionValueDrafts,
    handleOptionAdd,
    handleOptionRemove,
    handleOptionValueCommit,
    handleOptionValueKeyDown,
    handleOptionColorChange,
    handleOptionIconChange,
    handleOptionMove,
  };
};

export default useEnumOptionEditor;
